import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { MENU, MENU_NOTES } from "../data/content";
import { SectionLabel } from "./Section";
import { fadeUp, viewport } from "../lib/motion";
import { useOrder } from "../context/OrderContext";

const CATEGORIES = Object.keys(MENU);

const formatPrice = (price) => {
  if (!price) return "";
  if (price.includes("$")) return price;
  if (price.includes("/")) return `$${price.replace(" / ", " / $")}`;
  return `$${price}`;
};

const MenuPreview = () => {
  const [active, setActive] = useState(CATEGORIES[0]);
  const openOrder = useOrder();

  const selectTab = (cat) => (e) => {
    setActive(cat);
    e.currentTarget.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  };

  return (
    <section id="menu" className="relative py-16 sm:py-32 bg-surface/40" data-testid="menu-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8 sm:mb-12">
          <div className="max-w-2xl">
            <SectionLabel>Menu</SectionLabel>
            <motion.h2
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]"
            >
              Explore the <span className="text-highlight">menu.</span>
            </motion.h2>
          </div>
          <button
            onClick={openOrder}
            data-testid="menu-order-btn"
            className="self-start rounded-full bg-primary text-primary-foreground px-7 py-3.5 text-sm font-semibold tracking-wide transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
          >
            Order Online
          </button>
        </div>

        {/* Category tabs — swipeable single row on mobile */}
        <div className="relative -mx-6 md:mx-0 mb-8">
          <div
            className="flex gap-2.5 overflow-x-auto no-scrollbar px-6 md:px-0 md:flex-wrap"
            data-testid="menu-tabs"
          >
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={selectTab(cat)}
                data-testid={`menu-tab-${cat.toLowerCase()}`}
                className={`relative shrink-0 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-medium tracking-wide transition-colors duration-300 ${
                  active === cat
                    ? "text-background"
                    : "text-foreground/70 hover:text-foreground bg-background border border-surface"
                }`}
              >
                {active === cat && (
                  <motion.span
                    layoutId="menu-pill"
                    className="absolute inset-0 rounded-full bg-primary"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
                <span className="relative z-10">{cat}</span>
              </button>
            ))}
          </div>
          {/* edge fade on mobile */}
          <div className="md:hidden pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#f3ece1] to-transparent" />
        </div>

        {/* Mobile swipe hint */}
        <div
          className="md:hidden flex items-center justify-center gap-1.5 -mt-4 mb-7 text-[11px] uppercase tracking-[0.2em] text-foreground/45"
          data-testid="menu-scroll-hint"
        >
          <span>Swipe for more</span>
          <motion.span
            animate={{ x: [0, 4, 0] }}
            transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
            className="inline-flex text-highlight"
          >
            <ChevronRight size={14} strokeWidth={2.5} />
          </motion.span>
        </div>

        {/* Items */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            data-testid={`menu-panel-${active.toLowerCase()}`}
          >
            {MENU_NOTES[active] && (
              <p
                className="mb-6 text-[11px] sm:text-xs uppercase tracking-[0.22em] text-highlight font-semibold"
                data-testid={`menu-note-${active.toLowerCase()}`}
              >
                {MENU_NOTES[active]}
              </p>
            )}
            <div className="grid sm:grid-cols-2 gap-x-12">
              {MENU[active].map((item, i) =>
                item.heading ? (
                  <h5
                    key={`h-${i}`}
                    className="sm:col-span-2 mt-6 first:mt-0 mb-1 text-xs uppercase tracking-[0.2em] text-foreground/45 font-semibold"
                    data-testid={`menu-subheading-${item.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {item.heading}
                  </h5>
                ) : (
                  <div
                    key={item.name}
                    className="flex items-baseline justify-between gap-4 py-4 sm:py-5 border-b border-surface/80"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base sm:text-xl text-foreground leading-snug">{item.name}</h4>
                      <p className="mt-1 text-[13px] sm:text-sm text-foreground/60 leading-relaxed">{item.desc}</p>
                    </div>
                    <span className="text-base sm:text-lg font-semibold text-primary shrink-0 tabular-nums">
                      {formatPrice(item.price)}
                    </span>
                  </div>
                )
              )}
            </div>
          </motion.div>
        </AnimatePresence>
        <p className="mt-7 text-xs text-foreground/50 leading-relaxed">
          Specialty Coffee shown Small / Large. Brunch served Saturday & Sunday til 5pm. Full menus available in-store & online.
        </p>
      </div>
    </section>
  );
};

export default MenuPreview;
